import { useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { useMassMailContext } from '../context/MassMailContext';
import { SelectedResource, Contact, CustomSendFunction, EmailSendData } from '../types';
import { applyFooterSender } from '../utils/footerSender';
import { toast } from 'react-toastify';

export const useMassMailData = (
  customSendFunction?: CustomSendFunction,
  /**
   * Zet dit wanneer de aanroeper de uitkomst zelf toont (bv. reno-tickets: de composer
   * sluit en de pagina toont het verzendrapport). Het interne resultatenvenster van de
   * composer blijft dan dicht, zodat er niet twee rapporten over elkaar staan.
   */
  options?: { skipResultsModal?: boolean },
) => {
  const { session } = useSession();
  const { dispatch } = useMassMailContext();

  const fetchTemplateTypes = useCallback(async () => {
    if (!session) return;
    
    try {
      const response = await fetchWithAuth(
        `${API_URL}/mail-templates/types`,
        { method: 'GET' },
        session
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log('📁 Template types loaded:', data);
        dispatch({ type: 'SET_TEMPLATE_TYPES', payload: data });
      }
    } catch (error) {
      console.error('Error fetching template types:', error);
      toast.error('Kon template types niet laden');
    }
  }, [session, dispatch]);

  const fetchTemplates = useCallback(async () => {
    if (!session) return;
    
    try {
      const response = await fetchWithAuth(
        `${API_URL}/mail-templates`,
        { method: 'GET' },
        session
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log('📄 Templates loaded:', {
          count: data.length,
          templates: data.map((t: any) => ({ 
            id: t.id, 
            subject: t.subject, 
            templateType: t.templateType,
            label: t.label 
          }))
        });
        dispatch({ type: 'SET_TEMPLATES', payload: data });
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Kon templates niet laden');
    }
  }, [session, dispatch]);

  const fetchEmailSettings = useCallback(async () => {
    if (!session) return;
    
    try {
      const response = await fetchWithAuth(
        `${API_URL}/mail-templates/settings`,
        { method: 'GET' },
        session
      );
      
      if (response.ok) {
        const data = await response.json();
        dispatch({ type: 'SET_EMAIL_SETTINGS', payload: data });
      }
    } catch (error) {
      console.error('Error fetching email settings:', error);
      // Email settings are optional, so don't show error toast
    }
  }, [session, dispatch]);

  const extractContactsFromContracts = useCallback((activeContracts: any[], contextLabel: string): Contact[] => {
    const contactsMap = new Map<string, Contact>();

    activeContracts.forEach((contract: any) => {
      contract.contractContacts.forEach((contactRelation: any) => {
        if (!contactRelation || !contactRelation.resource) {
          return;
        }
        if (!contactRelation.relatedContact) {
          Sentry.captureMessage('Mass mail: contractContact missing relatedContact', {
            level: 'warning',
            tags: { hook: 'useMassMailData', context: contextLabel },
            extra: {
              contractId: contract?.id,
              resourceId: contactRelation.resource?.id,
            },
          });
          return;
        }

        const contactId = contactRelation.resource.id;
        const unitInfo = {
          id: contract.unit?.id || '',
          unitId: contract.unit?.id || '',
          unitNumber: contract.unit?.boxNumber || contract.unit?.id || '',
          referencePlus: contract.unit?.referencePlus || '',
          buildingId: contract.unit?.building?.id || '',
          buildingName: contract.unit?.building?.location?.formattedAddress || `Gebouw ${contract.unit?.building?.id}` || '',
        };

        if (contactsMap.has(contactId)) {
          const existingContact = contactsMap.get(contactId)!;
          if (!existingContact.units.some(u => u.unitId === unitInfo.unitId)) {
            existingContact.units.push(unitInfo);
          }
        } else {
          const newContact: Contact = {
            id: contactRelation.resource.id,
            firstName: contactRelation.relatedContact.firstName,
            lastName: contactRelation.relatedContact.lastName,
            name: contactRelation.resource.name,
            resourceId: contactRelation.resourceId,
            phoneMobile: contactRelation.resource.metadata1,
            email: contactRelation.relatedContact.email,
            units: [unitInfo],
            buildingId: unitInfo.buildingId,
            buildingName: unitInfo.buildingName,
            unitId: unitInfo.unitId,
            unitNumber: unitInfo.unitNumber,
          };
          contactsMap.set(contactId, newContact);
        }
      });
    });

    return Array.from(contactsMap.values());
  }, []);

  const fetchAllActiveContacts = useCallback(async (): Promise<Contact[]> => {
    if (!session) {
      console.log('❌ No session available for fetchAllActiveContacts');
      return [];
    }

    console.log('🌐 API Call: fetchAllActiveContacts (all tenants)');

    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/active`,
        { method: 'GET' },
        session,
      );

      if (response.ok) {
        const activeContracts = await response.json();
        const contacts = extractContactsFromContracts(activeContracts, 'fetchAllActiveContacts');
        console.log('🌐 API Result: Extracted', contacts.length, 'tenants from all active contracts');
        return contacts;
      }

      const errorText = await response.text();
      console.error('❌ Error response when fetching all active contracts:', response.status, errorText);
      Sentry.captureException(new Error('Failed to fetch all active contracts'), {
        tags: { hook: 'useMassMailData', method: 'fetchAllActiveContacts' },
        extra: { status: response.status, errorText },
      });
      toast.error('Fout bij het ophalen van alle huurders');
      throw new Error(`Failed to fetch all active contracts (status ${response.status})`);
    } catch (error) {
      console.error('❌ Error fetching all active contacts:', error);
      Sentry.captureException(error, {
        tags: { hook: 'useMassMailData', method: 'fetchAllActiveContacts' },
      });
      toast.error('Fout bij het ophalen van alle huurders');
      throw error;
    }
  }, [session, extractContactsFromContracts]);

  const fetchBuildingContacts = useCallback(async (buildingId: string): Promise<Contact[]> => {
    if (!session) {
      console.log('❌ No session available for fetchBuildingContacts');
      return [];
    }
    
    console.log('🌐 API Call: fetchBuildingContacts for', buildingId);
    
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/building/${buildingId}/active`,
        { method: "GET" },
        session,
      );
      
      if (response.ok) {
        const activeContracts = await response.json();
        const contactsMap = new Map<string, Contact>();

        activeContracts.forEach((contract: any) => {
          contract.contractContacts.forEach((contactRelation: any) => {
            if (!contactRelation || !contactRelation.resource) {
              return;
            }
            if (!contactRelation.relatedContact) {
              Sentry.captureMessage('Mass mail: contractContact missing relatedContact', {
                level: 'warning',
                tags: { hook: 'useMassMailData', method: 'fetchBuildingContacts' },
                extra: {
                  buildingId,
                  contractId: contract?.id,
                  resourceId: contactRelation.resource?.id,
                },
              });
              return;
            }

            const contactId = contactRelation.resource.id;
            const unitInfo = {
              id: contract.unit?.id || '',
              unitId: contract.unit?.id || '',
              unitNumber: contract.unit?.boxNumber || contract.unit?.id || '',
              referencePlus: contract.unit?.referencePlus || '',
              buildingId: contract.unit?.building?.id || '',
              buildingName: contract.unit?.building?.location?.formattedAddress || `Gebouw ${contract.unit?.building?.id}` || '',
            };

            if (contactsMap.has(contactId)) {
              // Add unit to existing contact
              const existingContact = contactsMap.get(contactId)!;
              // Check if this unit is already added to avoid duplicates
              if (!existingContact.units.some(u => u.unitId === unitInfo.unitId)) {
                existingContact.units.push(unitInfo);
              }
            } else {
              // Create new contact
              const newContact: Contact = {
                id: contactRelation.resource.id,
                firstName: contactRelation.relatedContact.firstName,
                lastName: contactRelation.relatedContact.lastName,
                name: contactRelation.resource.name,
                resourceId: contactRelation.resourceId,
                phoneMobile: contactRelation.resource.metadata1,
                email: contactRelation.relatedContact.email,
                units: [unitInfo],
                // Set primary building/unit info (first one)
                buildingId: unitInfo.buildingId,
                buildingName: unitInfo.buildingName,
                unitId: unitInfo.unitId,
                unitNumber: unitInfo.unitNumber,
              };
              contactsMap.set(contactId, newContact);
            }
          });
        });

        const contacts = Array.from(contactsMap.values());
        console.log('🌐 API Result: Extracted', contacts.length, 'contacts from building');
        console.log('📧 Building contacts email debug:', contacts.map(c => ({
          id: c.id,
          name: c.name,
          email: c.email,
          hasValidEmail: !!(c.email?.trim() && c.email?.trim() !== 'GSM-nummer onbekend')
        })));
        return contacts;
      } else {
        console.error("❌ Error response when fetching building contacts:", response.status);
        const errorText = await response.text();
        console.error("❌ Error details:", errorText);
        Sentry.captureException(new Error('Failed to fetch building contacts'), {
          tags: { hook: 'useMassMailData', method: 'fetchBuildingContacts' },
          extra: { buildingId, status: response.status, errorText },
        });
      }
    } catch (error) {
      console.error("❌ Error fetching building contacts:", error);
      Sentry.captureException(error, {
        tags: { hook: 'useMassMailData', method: 'fetchBuildingContacts' },
        extra: { buildingId },
      });
      toast.error("Fout bij het ophalen van contactpersonen voor gebouw");
    }
    return [];
  }, [session]);

  const fetchUnitContacts = useCallback(async (unitId: string): Promise<Contact[]> => {
    if (!session) {
      console.log('❌ No session available for fetchUnitContacts');
      return [];
    }
    
    console.log('🌐 API Call: fetchUnitContacts for', unitId);
    
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/unit/${unitId}/active`,
        { method: "GET" },
        session,
      );
      
      if (response.ok) {
        const activeContracts = await response.json();
        const contactsMap = new Map<string, Contact>();

        activeContracts.forEach((contract: any) => {
          contract.contractContacts.forEach((contactRelation: any) => {
            if (!contactRelation || !contactRelation.resource) {
              return;
            }
            if (!contactRelation.relatedContact) {
              Sentry.captureMessage('Mass mail: contractContact missing relatedContact', {
                level: 'warning',
                tags: { hook: 'useMassMailData', method: 'fetchUnitContacts' },
                extra: {
                  unitId,
                  contractId: contract?.id,
                  resourceId: contactRelation.resource?.id,
                },
              });
              return;
            }

            const contactId = contactRelation.resource.id;
            const unitInfo = {
              id: contract.unit?.id || '',
              unitId: contract.unit?.id || '',
              unitNumber: contract.unit?.boxNumber || contract.unit?.id || '',
              referencePlus: contract.unit?.referencePlus || '',
              buildingId: contract.unit?.building?.id || '',
              buildingName: contract.unit?.building?.location?.formattedAddress || `Gebouw ${contract.unit?.building?.id}` || '',
            };

            if (contactsMap.has(contactId)) {
              // Add unit to existing contact
              const existingContact = contactsMap.get(contactId)!;
              // Check if this unit is already added to avoid duplicates
              if (!existingContact.units.some(u => u.unitId === unitInfo.unitId)) {
                existingContact.units.push(unitInfo);
              }
            } else {
              // Create new contact
              const newContact: Contact = {
                id: contactRelation.resource.id,
                firstName: contactRelation.relatedContact.firstName,
                lastName: contactRelation.relatedContact.lastName,
                name: contactRelation.resource.name,
                resourceId: contactRelation.resourceId,
                phoneMobile: contactRelation.resource.metadata1,
                email: contactRelation.relatedContact.email,
                units: [unitInfo],
                // Set primary building/unit info (first one)
                buildingId: unitInfo.buildingId,
                buildingName: unitInfo.buildingName,
                unitId: unitInfo.unitId,
                unitNumber: unitInfo.unitNumber,
              };
              contactsMap.set(contactId, newContact);
            }
          });
        });

        const contacts = Array.from(contactsMap.values());
        console.log('🌐 API Result: Extracted', contacts.length, 'contacts from unit');
        console.log('📧 Unit contacts email debug:', contacts.map(c => ({
          id: c.id,
          name: c.name,
          email: c.email,
          hasValidEmail: !!(c.email?.trim() && c.email?.trim() !== 'GSM-nummer onbekend')
        })));
        return contacts;
      } else {
        console.error("❌ Error response when fetching unit contacts:", response.status);
        const errorText = await response.text();
        console.error("❌ Error details:", errorText);
        Sentry.captureException(new Error('Failed to fetch unit contacts'), {
          tags: { hook: 'useMassMailData', method: 'fetchUnitContacts' },
          extra: { unitId, status: response.status, errorText },
        });
      }
    } catch (error) {
      console.error("❌ Error fetching unit contacts:", error);
      Sentry.captureException(error, {
        tags: { hook: 'useMassMailData', method: 'fetchUnitContacts' },
        extra: { unitId },
      });
      toast.error("Fout bij het ophalen van contactpersonen voor unit");
    }
    return [];
  }, [session]);

  const loadContactsForResource = useCallback(async (resource: SelectedResource): Promise<Contact[]> => {
    try {
      let newContacts: Contact[] = [];
      
      if (resource.type === 'building') {
        newContacts = await fetchBuildingContacts(resource.resourceId);
      } else if (resource.type === 'unit') {
        newContacts = await fetchUnitContacts(resource.resourceId);
      } else if (resource.type === 'all-tenants') {
        newContacts = await fetchAllActiveContacts();
      }

      console.log(`Loaded ${newContacts.length} contacts from ${resource.type}: ${resource.name}`);
      return newContacts;
      
    } catch (error) {
      console.error('Error loading contacts for resource:', error);
      toast.error('Fout bij het laden van contacten');
      return [];
    }
  }, [fetchBuildingContacts, fetchUnitContacts, fetchAllActiveContacts]);

  const getContactsToRemove = useCallback(async (resourceToRemove: SelectedResource): Promise<Contact[]> => {
    try {
      let contactsToRemove: Contact[] = [];
      if (resourceToRemove.type === 'building') {
        contactsToRemove = await fetchBuildingContacts(resourceToRemove.resourceId);
      } else if (resourceToRemove.type === 'unit') {
        contactsToRemove = await fetchUnitContacts(resourceToRemove.resourceId);
      } else if (resourceToRemove.type === 'all-tenants') {
        contactsToRemove = await fetchAllActiveContacts();
      }

      console.log(`Found ${contactsToRemove.length} contacts to remove from ${resourceToRemove.type}: ${resourceToRemove.name}`);
      return contactsToRemove;
      
    } catch (error) {
      console.error('Error getting contacts to remove:', error);
      return [];
    }
  }, [fetchBuildingContacts, fetchUnitContacts, fetchAllActiveContacts]);

  // Helper function to process placeholders in subject/content
  const processPlaceholders = (text: string, contact: any, emailData: EmailSendData): string => {
    let processed = text;
    
    // Safely get contact properties
    const contactName = contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unknown Contact';
    const contactEmail = contact.email || '';
    const contactFirstName = contact.firstName || '';
    const contactLastName = contact.lastName || '';
    
    const contactPhone = contact.phoneMobile ? String(contact.phoneMobile).trim() : '';

    // Replace basic contact placeholders
    processed = processed.replace(/{contact\.firstName}/g, contactFirstName);
    processed = processed.replace(/{contact\.lastName}/g, contactLastName);
    processed = processed.replace(/{contact\.fullName}/g, contactName);
    processed = processed.replace(/{contact\.email}/g, contactEmail);
    processed = processed.replace(/{contact\.phone}/g, contactPhone);
    
    // Replace context-specific placeholders (e.g., {ticketId}, {ticketTitle})
    if (emailData.contextPlaceholders) {
      Object.entries(emailData.contextPlaceholders).forEach(([placeholder, value]) => {
        const regex = new RegExp(`\\{${placeholder}\\}`, 'g');
        processed = processed.replace(regex, value);
      });
    }
    
    // Dutch legacy placeholders
    const contactReplacements: { [key: string]: string } = {
      '{ResourceID}': (contact.resourceId ? String(contact.resourceId).trim() : '') || '',
      '{VolledigeNaam}': contactName,
      '{Voornaam}': contactFirstName,
      '{Achternaam}': contactLastName,
      '{MobielNummer}': (contact.phoneMobile ? String(contact.phoneMobile).trim() : '') || '',
      '{Email}': contactEmail
    };
    
    Object.entries(contactReplacements).forEach(([placeholder, value]) => {
      const regex = new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g');
      processed = processed.replace(regex, value);
    });
    
    // Replace entity placeholders
    if (emailData.mentionedEntities) {
      emailData.mentionedEntities.forEach((entity: any) => {
        if (entity.fields && entity.uniqueId) {
          Object.entries(entity.fields).forEach(([fieldName, fieldData]: [string, any]) => {
            if (fieldData && fieldData.emailEnabled) {
              const placeholder = `{${entity.uniqueId}.${fieldName}}`;
              const value = fieldData.value !== null ? fieldData.value : '';
              const regex = new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g');
              processed = processed.replace(regex, value);
            }
          });
        }
      });
    }
    
    // Replace parameter placeholders
    if (emailData.parameters && emailData.parameterValues) {
      emailData.parameters.forEach((param: any) => {
        const paramValue = emailData.parameterValues[param.id];
        const value = param.type === 'global' 
          ? (typeof paramValue === 'string' ? paramValue : '')
          : (typeof paramValue === 'object' && paramValue !== null ? (paramValue as { [key: string]: string })[contact.id] : '');
        
        if (value) {
          const regex = new RegExp(param.placeholder.replace(/[{}]/g, '\\$&'), 'g');
          processed = processed.replace(regex, value);
        }
      });
    }
    
    return processed;
  };

  const sendMassEmail = useCallback(async (emailData: EmailSendData) => {
    if (!session) return;
    
    dispatch({ type: 'SET_IS_SENDING', payload: true });
    
    try {
      // If custom send function is provided, use it instead of default logic
      if (customSendFunction) {
        console.log('📤 Using custom send function');
        
        // Process placeholders for the first contact (helpdesk has single recipient)
        const contact = emailData.contacts[0];
        const processedEmailData = {
          ...emailData,
          subject: processPlaceholders(emailData.subject, contact, emailData),
          content: processPlaceholders(emailData.content, contact, emailData)
        };
        
        console.log('📝 Processed email data:', {
          originalSubject: emailData.subject,
          processedSubject: processedEmailData.subject,
          hasContent: !!processedEmailData.content
        });
        
        const result = await customSendFunction(processedEmailData);
        
        // Show success
        dispatch({ type: 'SET_EMAIL_SEND_RESULTS', payload: result });
        
        const sendResults = {
          totalSent: emailData.contacts.length,
          totalFailed: 0,
          totalContacts: emailData.contacts.length,
          timestamp: new Date().toISOString(),
          senderEmail: emailData.senderEmail,
          subject: processedEmailData.subject,
          ccContacts: emailData.ccContacts?.length && emailData.ccContacts?.length > 0 ? emailData.ccContacts : undefined,
          bccContacts: emailData.bccContacts?.length && emailData.bccContacts?.length  > 0 ? emailData.bccContacts : undefined,
          results: emailData.contacts.map((contact: any) => ({
            contactId: contact.id,
            contactName: contact.name,
            email: contact.email,
            status: 'success' as const
          }))
        };

        dispatch({ type: 'SET_SEND_RESULTS', payload: sendResults });
        if (!options?.skipResultsModal) {
          dispatch({ type: 'SET_RESULTS_MODAL_OPEN', payload: true });
        }

        return result;
      }
      
      // Default logic: bulk send via /email/send-mass-email
      console.log('📤 Using default mass-mail send');
      
      // Upload attachments first if any exist (like legacy modal)
      let uploadedAttachments: any[] = [];
      if (emailData.attachedFiles && emailData.attachedFiles.length > 0) {
        console.log(`📎 Uploading ${emailData.attachedFiles.length} attachments...`);
        
        const formData = new FormData();
        emailData.attachedFiles.forEach((attachedFile: any) => {
          formData.append('files', attachedFile.file);
        });

        const uploadResponse = await fetchWithAuth(
          `${API_URL}/email/upload-attachments`,
          {
            method: "POST",
            body: formData, // Don't set Content-Type header, let browser set it with boundary
          },
          session
        );

        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          uploadedAttachments = uploadResult.attachments;
          console.log(`✅ Successfully uploaded ${uploadedAttachments.length} attachments`);
        } else {
          const errorData = await uploadResponse.json();
          console.error("Attachment upload failed:", errorData);
          toast.error(`Fout bij uploaden bijlagen: ${errorData.message || 'Onbekende fout'}`);
          return;
        }
      }
      
      // Process recipients like the legacy modal does
      
      const processedRecipients = emailData.contacts.flatMap((contact: any, index: number) => {
        if (!contact) {
          console.error(`❌ Contact at index ${index} is undefined/null`);
          return [];
        }


        // Basic parameter replacement for now (legacy has more complex logic)
        let processedSubject = emailData.subject || '';
        let processedContent = emailData.content || '';

        // Safely get contact properties
        const contactName = contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unknown Contact';
        const contactEmail = contact.email || '';
        const contactFirstName = contact.firstName || '';
        const contactLastName = contact.lastName || '';
        const contactPhone = contact.phoneMobile ? String(contact.phoneMobile).trim() : '';

        // Replace basic contact placeholders (recipient-specific)
        // Both English-style and Dutch legacy placeholders for backward compatibility

        // English-style placeholders
        processedSubject = processedSubject.replace(/{contact\.firstName}/g, contactFirstName);
        processedSubject = processedSubject.replace(/{contact\.lastName}/g, contactLastName);
        processedSubject = processedSubject.replace(/{contact\.fullName}/g, contactName);
        processedSubject = processedSubject.replace(/{contact\.phone}/g, contactPhone);
        processedSubject = processedSubject.replace(/{contact\.email}/g, contactEmail);

        processedContent = processedContent.replace(/{contact\.firstName}/g, contactFirstName);
        processedContent = processedContent.replace(/{contact\.lastName}/g, contactLastName);
        processedContent = processedContent.replace(/{contact\.fullName}/g, contactName);
        processedContent = processedContent.replace(/{contact\.email}/g, contactEmail);
        processedContent = processedContent.replace(/{contact\.phone}/g, contactPhone);

        // Replace context-specific placeholders (e.g., ticket data)
        if (emailData.contextPlaceholders) {
          Object.entries(emailData.contextPlaceholders).forEach(([placeholder, value]) => {
            const regex = new RegExp(`\\{${placeholder}\\}`, 'g');
            processedSubject = processedSubject.replace(regex, value);
            processedContent = processedContent.replace(regex, value);
          });
        }

        // Dutch legacy placeholders (from legacy MassMailModal)
        const contactReplacements = {
          '{ResourceID}': (contact.resourceId ? String(contact.resourceId).trim() : '') || '',
          '{VolledigeNaam}': contactName,
          '{Voornaam}': contactFirstName,
          '{Achternaam}': contactLastName,
          '{MobielNummer}': (contact.phoneMobile ? String(contact.phoneMobile).trim() : '') || '',
          '{Email}': contactEmail
        };

        Object.entries(contactReplacements).forEach(([placeholder, value]) => {
          const regex = new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g');
          processedSubject = processedSubject.replace(regex, value);
          processedContent = processedContent.replace(regex, value);
        });


        // Replace entity placeholders (like {company1.name}, {contact1.firstName})
        if (emailData.mentionedEntities) {
          emailData.mentionedEntities.forEach((entity: any) => {
            if (entity.fields && entity.uniqueId) {

              Object.entries(entity.fields).forEach(([fieldName, fieldData]: [string, any]) => {
                if (fieldData && fieldData.emailEnabled) {
                  const placeholder = `{${entity.uniqueId}.${fieldName}}`;
                  const value = fieldData.value !== null ? fieldData.value : '';
                  const regex = new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g');

                  processedSubject = processedSubject.replace(regex, value);
                  processedContent = processedContent.replace(regex, value);
                }
              });
            }
          });
        }

        // Replace parameter placeholders
        if (emailData.parameters && emailData.parameterValues) {
          emailData.parameters.forEach((param: any) => {
            const paramValue = emailData.parameterValues[param.id];
            const value = param.type === 'global'
              ? (typeof paramValue === 'string' ? paramValue : '')
              : (typeof paramValue === 'object' && paramValue !== null ? (paramValue as { [key: string]: string })[contact.id] : '');


            if (value) {
              const regex = new RegExp(param.placeholder.replace(/[{}]/g, '\\$&'), 'g');
              processedSubject = processedSubject.replace(regex, value);
              processedContent = processedContent.replace(regex, value);
            }
          });

          // Also check for any unmatched parameters in the content
          const unmatchedParams = processedContent.match(/\{[^}]+\}/g);
          if (unmatchedParams) {
            // Try to replace any remaining common placeholders
            unmatchedParams.forEach((param: string) => {
              if (param === '{VolledigeNaam}') {
                processedContent = processedContent.replace(/{VolledigeNaam}/g, contactName);
              }
            });
          }
        }

        // Combine header, content, and footer (like legacy version).
        // Het contactadres in de standaardfooter volgt de afzender — een mail vanaf een
        // ander postvak mag onderaan niet naar ella@yally.be verwijzen (footerSender.ts).
        const fullEmailContent = [
          emailData.emailSettings?.headerHtml || '',
          processedContent,
          applyFooterSender(emailData.emailSettings?.footerHtml, emailData.senderEmail)
        ].filter(Boolean).join('');

        // Fix links to ensure they have proper protocols for email clients (like legacy)
        const fixedEmailContent = fullEmailContent.replace(
          /href="([^"]+)"/g,
          (match, url) => {
            // Only add https:// if the URL doesn't already have a protocol
            if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('mailto:') && !url.startsWith('#') && !url.startsWith('/')) {
              return `href="https://${url}"`;
            }
            return match;
          }
        );

        // Handle comma-separated email addresses
        // Split on comma, trim whitespace, and filter out empty strings
        const emailAddresses = contactEmail
          .split(',')
          .map((email: string) => email.trim())
          .filter((email: string) => email.length > 0);

        // Log if we found multiple emails
        if (emailAddresses.length > 1) {
          console.log(`📧 Found ${emailAddresses.length} email addresses for contact ${contactName}:`, emailAddresses);
        }

        // Create a recipient object for each email address
        return emailAddresses.map((emailAddress: string) => ({
          emailAddress: {
            address: emailAddress,
            name: contactName
          },
          subject: processedSubject,
          content: fixedEmailContent,
          contactId: contact.id || `unknown_${index}`,
          contactName: contactName
        }));
      }).filter(Boolean); // Remove any null entries

      // Map CC/BCC contact arrays to Graph-compatible recipient format
      const toGraphRecipients = (contacts: Array<{ name: string; email: string }>) =>
        (contacts || []).map(c => ({ emailAddress: { address: c.email, name: c.name } }));

      const ccRecipients = toGraphRecipients(emailData.ccContacts || []);
      const bccRecipients = toGraphRecipients(emailData.bccContacts || []);

      // Use the same structure as legacy modal (EXACT MATCH)
      const requestBody = {
        senderEmail: emailData.senderEmail,
        processedRecipients: processedRecipients,
        buildingId: null, // No single building - multiple resources supported
        attachments: uploadedAttachments, // Include uploaded attachments
        ccRecipients: ccRecipients.length > 0 ? ccRecipients : undefined,
        bccRecipients: bccRecipients.length > 0 ? bccRecipients : undefined,
        metadata: {
          totalContacts: emailData.contacts.length,
          validContacts: emailData.contacts.length,
          mentionedEntities: emailData.mentionedEntities?.length || 0,
          attachmentCount: uploadedAttachments.length
        }
      };


      const response = await fetchWithAuth(
        `${API_URL}/email/send-mass-email`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        },
        session
      );

      if (response.ok) {
        const data = await response.json();
        dispatch({ type: 'SET_EMAIL_SEND_RESULTS', payload: data });
        
        // Create send results data for the modal
        const sendResults = {
          totalSent: emailData.contacts.length,
          totalFailed: 0,
          totalContacts: emailData.contacts.length,
          timestamp: new Date().toISOString(),
          senderEmail: emailData.senderEmail,
          subject: emailData.subject,
          ccContacts: emailData.ccContacts?.length  && emailData.ccContacts?.length > 0 ? emailData.ccContacts : undefined,
          bccContacts: emailData.bccContacts?.length && emailData.bccContacts?.length > 0 ? emailData.bccContacts : undefined,
          results: emailData.contacts.map((contact: any) => ({
            contactId: contact.id,
            contactName: contact.name,
            email: contact.email,
            status: 'success' as const
          }))
        };
        
        // Show results modal instead of toast
        dispatch({ type: 'SET_SEND_RESULTS', payload: sendResults });
        dispatch({ type: 'SET_RESULTS_MODAL_OPEN', payload: true });
        
        return data;
      } else {
        const errorData = await response.json();
        console.error("Server error:", errorData);
        throw new Error(errorData.message || 'Onbekende fout');
      }
    } catch (error) {
      console.error('Error sending mass email:', error);
      const errorMessage = error instanceof Error ? error.message : 'Onbekende fout';
      toast.error(`Fout bij verzenden: ${errorMessage}`);
      throw error;
    } finally {
      dispatch({ type: 'SET_IS_SENDING', payload: false });
    }
  }, [session, dispatch, customSendFunction, options?.skipResultsModal]);

  return {
    fetchTemplateTypes,
    fetchTemplates,
    fetchEmailSettings,
    fetchBuildingContacts,
    fetchUnitContacts,
    fetchAllActiveContacts,
    loadContactsForResource,
    getContactsToRemove,
    sendMassEmail,
  };
};
